<template>
  <transition
    name="play-modal"
    @after-leave="onAfterLeave"
  >
    <div
      v-if="visible"
      class="play-modal"
      @mousedown.self="onBackdrop"
    >
      <div
        ref="dialog"
        class="play-modal__dialog"
        :class="[`play-modal__dialog--${size}`, { 'play-modal__dialog--scrollable': scrollable }]"
        role="dialog"
        aria-modal="true"
        :aria-label="title || null"
        tabindex="-1"
      >
        <header
          v-if="!hideHeader"
          class="play-modal__header"
        >
          <slot name="header">
            <h2 class="play-modal__title">
              {{ title }}
            </h2>
            <button
              type="button"
              class="play-modal__close"
              :aria-label="closeLabel"
              @click="onCloseButton"
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              ><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
          </slot>
        </header>

        <div
          class="play-modal__body"
          :class="bodyClass"
        >
          <slot />
        </div>

        <footer
          v-if="!hideFooter"
          class="play-modal__footer"
        >
          <slot name="footer">
            <PlayButton
              v-if="!okOnly"
              variant="ghost"
              @click="onCancel"
            >
              {{ cancelTitle }}
            </PlayButton>
            <PlayButton
              :variant="okVariant"
              :disabled="okDisabled || okLoading"
              @click="onOk"
            >
              {{ okTitle }}
            </PlayButton>
          </slot>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import PlayButton from '@/components/play/PlayButton.vue'

/**
 * PlayModal — dialog / overlay for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces
 * bootstrap-vue's <b-modal>. Driven by v-model (`visible`) instead of the global
 * `$bvModal.show/hide` registry — a controlled boolean is cleaner and Vue 3-safe
 * (migrate `$bvModal.show('id')` call sites to flipping that boolean).
 *
 * `title` + the default OK/Cancel footer cover the common case; the `header`,
 * default, and `footer` slots replace each region for full control (e.g. the
 * booking modals' custom header + footer). Handles scroll-lock, a focus trap,
 * Esc/backdrop close, focus restore, and a bounce pop-in.
 *
 * VUE3-MIGRATION:
 *  - `model: { prop: 'visible', event: 'change' }` → Vue 3 `modelValue` /
 *    `update:modelValue`; change this block + the emit name.
 *  - Transition classes use Vue 2 names (`-enter`/`-leave`); Vue 3 renames them
 *    to `-enter-from`/`-leave-from` (see the <style> block).
 *  - The overlay uses `position: fixed` (no portal). If a transformed ancestor
 *    ever clips it, wrap the root in `<teleport to="body">` on Vue 3.
 */
export default {
  name: 'PlayModal',
  components: { PlayButton },
  model: {
    prop: 'visible',
    event: 'change',
  },
  props: {
    /** Open state (v-model target) */
    visible: {
      type: Boolean,
      default: false,
    },
    /** Simple header title (ignored when the `header` slot is used) */
    title: {
      type: String,
      default: '',
    },
    /** Dialog width */
    size: {
      type: String,
      default: 'md',
      validator: v => ['sm', 'md', 'lg', 'xl'].includes(v),
    },
    okTitle: {
      type: String,
      default: 'OK',
    },
    cancelTitle: {
      type: String,
      default: 'Cancel',
    },
    /** Footer OK button color role */
    okVariant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'highlight', 'secondary', 'ghost'].includes(v),
    },
    /** Show only the OK button */
    okOnly: {
      type: Boolean,
      default: false,
    },
    okDisabled: {
      type: Boolean,
      default: false,
    },
    /** Disable OK to guard against double-submit while an action is in-flight */
    okLoading: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    noCloseOnBackdrop: {
      type: Boolean,
      default: false,
    },
    noCloseOnEsc: {
      type: Boolean,
      default: false,
    },
    /** Header/footer stay fixed; the body scrolls */
    scrollable: {
      type: Boolean,
      default: false,
    },
    /** Passthrough class for the scrolling body region */
    bodyClass: {
      type: String,
      default: '',
    },
    /** Accessible label for the default close (X) button */
    closeLabel: {
      type: String,
      default: 'Close',
    },
  },
  data() {
    return {
      previouslyFocused: null,
      prevBodyOverflow: '',
      prevBodyPad: '',
    }
  },
  watch: {
    visible(val) {
      if (val) this.handleOpen()
      else this.handleClose()
    },
  },
  mounted() {
    if (this.visible) this.handleOpen()
  },
  beforeDestroy() {
    // Safety net if the modal is torn down while still open.
    document.removeEventListener('keydown', this.onKeydown, true)
    if (this.visible) this.unlockScroll()
  },
  methods: {
    // ── Lifecycle ──
    handleOpen() {
      this.$emit('show')
      this.previouslyFocused = document.activeElement
      this.lockScroll()
      document.addEventListener('keydown', this.onKeydown, true)
      this.$nextTick(() => {
        const target = this.firstFocusable() || this.$refs.dialog
        if (target) target.focus()
        this.$emit('shown')
      })
    },
    handleClose() {
      this.$emit('hide')
      document.removeEventListener('keydown', this.onKeydown, true)
      this.unlockScroll()
      const el = this.previouslyFocused
      if (el && typeof el.focus === 'function') el.focus()
      this.previouslyFocused = null
    },
    onAfterLeave() {
      this.$emit('hidden')
    },

    // ── Close triggers ──
    close() {
      this.$emit('change', false)
    },
    onOk() {
      this.$emit('ok')
      this.close()
    },
    onCancel() {
      this.$emit('cancel')
      this.close()
    },
    onCloseButton() {
      this.$emit('cancel')
      this.close()
    },
    onBackdrop() {
      if (!this.noCloseOnBackdrop) this.close()
    },

    // ── Keyboard: Esc to close, Tab trapped within the dialog ──
    onKeydown(e) {
      if (e.key === 'Escape') {
        if (!this.noCloseOnEsc) {
          e.preventDefault()
          this.close()
        }
        return
      }
      if (e.key !== 'Tab') return

      const items = this.focusable()
      if (!items.length) {
        e.preventDefault()
        if (this.$refs.dialog) this.$refs.dialog.focus()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      if (e.shiftKey && (active === first || active === this.$refs.dialog)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    },
    focusable() {
      const { dialog } = this.$refs
      if (!dialog) return []
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'textarea:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',')
      return Array.from(dialog.querySelectorAll(selector))
        .filter(el => el.offsetParent !== null)
    },
    firstFocusable() {
      return this.focusable()[0] || null
    },

    // ── Body scroll lock (compensate for the scrollbar to avoid shift) ──
    lockScroll() {
      const sbw = window.innerWidth - document.documentElement.clientWidth
      this.prevBodyOverflow = document.body.style.overflow
      this.prevBodyPad = document.body.style.paddingRight
      document.body.style.overflow = 'hidden'
      if (sbw > 0) document.body.style.paddingRight = `${sbw}px`
    },
    unlockScroll() {
      document.body.style.overflow = this.prevBodyOverflow
      document.body.style.paddingRight = this.prevBodyPad
    },
  },
}
</script>

<style lang="scss" scoped>
.play-modal {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(27, 20, 34, .55);
  -webkit-backdrop-filter: blur(2px);
  backdrop-filter: blur(2px);
  overflow-y: auto;

  &__dialog {
    --modal-w: 520px;
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: var(--modal-w);
    max-height: calc(100vh - 40px);
    background: var(--surface-card);
    border: var(--border-w-bold) solid var(--border-ink);
    border-radius: var(--radius-xl);
    box-shadow: 0 10px 0 var(--border-ink);
    overflow: hidden;

    &--sm { --modal-w: 380px; }
    &--md { --modal-w: 520px; }
    &--lg { --modal-w: 720px; }
    &--xl { --modal-w: 920px; }

    &:focus { outline: none; }
  }

  /* ── Header ── */
  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-shrink: 0;
    padding: 20px 22px;
    border-bottom: var(--border-w) solid var(--border-ink);
  }

  &__title {
    margin: 0;
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 20px;
    letter-spacing: -.02em;
    line-height: 1.15;
    color: var(--text-strong);
  }

  &__close {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    margin: -2px -4px 0 0;
    padding: 0;
    background: var(--surface-bg);
    border: var(--border-w) solid var(--border-ink);
    border-radius: var(--radius-sm);
    box-shadow: 0 3px 0 var(--border-ink);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition:
      transform var(--dur-fast) var(--ease-bounce),
      box-shadow var(--dur-fast) var(--ease-out);

    svg {
      width: 16px;
      height: 16px;
      fill: none;
      stroke: var(--text-strong);
      stroke-width: 3;
      stroke-linecap: round;
    }

    &:hover { box-shadow: 0 4px 0 var(--border-ink); }
    &:active { transform: translateY(2px); box-shadow: var(--shadow-press); }
    &:focus-visible { outline: 2px solid var(--c-secondary); outline-offset: 2px; }
  }

  /* ── Body ── */
  &__body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    padding: 22px;
    font-family: var(--font-body);
    color: var(--text-body);
    line-height: 1.55;
  }

  /* ── Footer ── */
  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    flex-shrink: 0;
    padding: 18px 22px;
    border-top: var(--border-w) solid var(--border-ink);
  }
}

/* ── Transition (Vue 2 class names — see VUE3-MIGRATION) ── */
.play-modal-enter-active,
.play-modal-leave-active {
  transition: opacity var(--dur) var(--ease-out);
}

.play-modal-enter-active .play-modal__dialog,
.play-modal-leave-active .play-modal__dialog {
  transition: transform var(--dur) var(--ease-bounce);
}

.play-modal-enter,
.play-modal-leave-to { opacity: 0; }

.play-modal-enter .play-modal__dialog { transform: translateY(20px) scale(.95); }
.play-modal-leave-to .play-modal__dialog { transform: translateY(12px) scale(.98); }

@media (prefers-reduced-motion: reduce) {
  .play-modal-enter-active,
  .play-modal-leave-active,
  .play-modal-enter-active .play-modal__dialog,
  .play-modal-leave-active .play-modal__dialog,
  .play-modal__close { transition: none; }

  .play-modal-enter .play-modal__dialog,
  .play-modal-leave-to .play-modal__dialog { transform: none; }
}
</style>
